// Lightweight Web Audio synth: no assets, generated SFX + music loop.
export class AudioManager {
  constructor() {
    this.ctx = null;
    this.sfxOn = true;
    this.musicOn = true;
    this.master = null;
    this.musicGain = null;
    this.sfxGain = null;
    this._musicTimer = null;
    this._step = 0;
  }

  _ensure() {
    if (this.ctx) return;
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    this.ctx = new AC();
    this.master = this.ctx.createGain();
    this.master.gain.value = 0.8;
    this.master.connect(this.ctx.destination);
    this.sfxGain = this.ctx.createGain();
    this.sfxGain.gain.value = 0.9;
    this.sfxGain.connect(this.master);
    this.musicGain = this.ctx.createGain();
    this.musicGain.gain.value = 0.28;
    this.musicGain.connect(this.master);
  }

  resume() {
    this._ensure();
    if (this.ctx && this.ctx.state === 'suspended') this.ctx.resume();
  }

  setSfx(on) { this.sfxOn = on; }
  setMusic(on) {
    this.musicOn = on;
    if (on) this.startMusic(); else this.stopMusic();
  }

  _tone(freq, dur, type = 'sine', vol = 0.5, slideTo = null) {
    if (!this.sfxOn) return;
    this._ensure();
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    const o = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    o.type = type;
    o.frequency.setValueAtTime(freq, t);
    if (slideTo) o.frequency.exponentialRampToValueAtTime(slideTo, t + dur);
    g.gain.setValueAtTime(vol, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    o.connect(g); g.connect(this.sfxGain);
    o.start(t); o.stop(t + dur + 0.02);
  }

  _noise(dur, vol = 0.5, filterFreq = 1200) {
    if (!this.sfxOn) return;
    this._ensure();
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    const n = Math.floor(this.ctx.sampleRate * dur);
    const buf = this.ctx.createBuffer(1, n, this.ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < n; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / n);
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    const f = this.ctx.createBiquadFilter();
    f.type = 'lowpass'; f.frequency.value = filterFreq;
    const g = this.ctx.createGain();
    g.gain.value = vol;
    src.connect(f); f.connect(g); g.connect(this.sfxGain);
    src.start(t);
  }

  play(name) {
    switch (name) {
      case 'punch': this._noise(0.12, 0.5, 800); this._tone(180, 0.1, 'square', 0.3, 90); break;
      case 'kick': this._noise(0.18, 0.7, 500); this._tone(120, 0.16, 'square', 0.4, 60); break;
      case 'shake': this._tone(300, 0.08, 'triangle', 0.25, 260); break;
      case 'break': this._noise(0.35, 0.9, 1600); this._tone(90, 0.3, 'sawtooth', 0.4, 40); break;
      case 'snackfall': this._tone(660, 0.08, 'triangle', 0.25, 880); break;
      case 'collect': this._tone(880, 0.09, 'sine', 0.4, 1320); break;
      case 'golden': this._tone(660, 0.1, 'sine', 0.5, 990); setTimeout(() => this._tone(1320, 0.14, 'sine', 0.5, 1760), 90); break;
      case 'detect': this._tone(440, 0.14, 'sawtooth', 0.35, 660); break;
      case 'chase': this._tone(220, 0.2, 'square', 0.35, 330); setTimeout(() => this._tone(260, 0.2, 'square', 0.35, 200), 180); break;
      case 'caught': this._tone(400, 0.4, 'sawtooth', 0.5, 80); this._noise(0.4, 0.5, 700); break;
      case 'power': this._tone(520, 0.1, 'sine', 0.4, 780); setTimeout(() => this._tone(780, 0.12, 'sine', 0.4, 1040), 80); break;
      case 'escape': this._tone(500, 0.1, 'triangle', 0.4, 750); setTimeout(() => this._tone(750, 0.12, 'triangle', 0.4, 1000), 90); break;
      case 'tick': this._tone(880, 0.06, 'square', 0.3); break;
      case 'go': this._tone(523, 0.12, 'square', 0.5); setTimeout(() => this._tone(784, 0.2, 'square', 0.5), 120); break;
      case 'end': this._tone(392, 0.2, 'triangle', 0.5); setTimeout(() => this._tone(523, 0.2, 'triangle', 0.5), 180); setTimeout(() => this._tone(659, 0.3, 'triangle', 0.5), 360); break;
      case 'buy': this._tone(700, 0.08, 'sine', 0.3, 500); break;
      default: break;
    }
  }

  startMusic() {
    if (!this.musicOn) return;
    this._ensure();
    if (!this.ctx || this._musicTimer) return;
    const bass = [55, 55, 82.4, 73.4];
    const lead = [220, 261.6, 329.6, 392, 329.6, 261.6, 220, 196];
    const beat = 0.26;
    const tick = () => {
      if (!this.ctx) return;
      const s = this._step;
      // bass on downbeats
      if (s % 2 === 0) {
        const t = this.ctx.currentTime;
        const o = this.ctx.createOscillator();
        const g = this.ctx.createGain();
        o.type = 'triangle';
        o.frequency.value = bass[(s / 2) % bass.length];
        g.gain.setValueAtTime(0.5, t);
        g.gain.exponentialRampToValueAtTime(0.001, t + beat * 1.6);
        o.connect(g); g.connect(this.musicGain);
        o.start(t); o.stop(t + beat * 1.7);
      }
      // lead arpeggio
      const t = this.ctx.currentTime;
      const o = this.ctx.createOscillator();
      const g = this.ctx.createGain();
      o.type = 'square';
      o.frequency.value = lead[s % lead.length];
      g.gain.setValueAtTime(0.14, t);
      g.gain.exponentialRampToValueAtTime(0.001, t + beat * 0.9);
      o.connect(g); g.connect(this.musicGain);
      o.start(t); o.stop(t + beat);
      this._step = (s + 1) % 32;
    };
    this._musicTimer = setInterval(tick, beat * 1000);
  }

  stopMusic() {
    if (this._musicTimer) { clearInterval(this._musicTimer); this._musicTimer = null; }
  }
}

export const audio = new AudioManager();
